import { deleteDoc, deleteField, getDoc, getDocs, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where, } from "firebase/firestore";
import { asManagedProfileId, asTemplateId, err, newId, notFound, ok, } from "@pack-it-up/shared";
import { participantsToDomain, toDomainCategory, toDomainItem, toDomainTodo } from "../mappers/index.js";
import { logSnapshotError, toDomainError } from "./support.js";
// Templates carry only TemplateSummary in the domain; the doc is projected inline.
const toSummary = (raw, userId) => {
    const id = raw.id;
    const name = raw.name;
    if (typeof id !== "string" || typeof name !== "string")
        return null;
    const members = raw.members ?? {};
    const role = members[userId]?.role ?? "viewer";
    const color = raw.color;
    const emoji = raw.emoji;
    return {
        id: asTemplateId(id),
        name,
        ...(typeof color === "string" ? { color: color } : {}),
        ...(typeof emoji === "string" ? { emoji } : {}),
        role,
        archived: raw.archived === true,
        // Issue #185 — the template's default travelers ride along on the summary the list
        // already streams; the editor needs them to offer an assignee picker.
        participants: participantsToDomain(raw.participants),
    };
};
export const makeTemplateRepo = (db, p, session) => ({
    observeMyTemplates: (userId, opts) => ({
        subscribe(next) {
            const q = query(p.templates(), where("memberUserIds", "array-contains", userId), where("archived", "==", opts.archived));
            const unsub = onSnapshot(q, (snap) => {
                const out = [];
                for (const d of snap.docs) {
                    const s = toSummary(d.data(), userId);
                    if (s)
                        out.push(s);
                }
                next(out);
            }, logSnapshotError("myTemplates"));
            return unsub;
        },
    }),
    // Name search is over the caller's own templates (no global scan).
    async searchTemplates(userId, queryText) {
        try {
            const snap = await getDocs(query(p.templates(), where("memberUserIds", "array-contains", userId)));
            const q = queryText.trim().toLowerCase();
            const out = [];
            for (const d of snap.docs) {
                const s = toSummary(d.data(), userId);
                if (s && s.name.toLowerCase().includes(q))
                    out.push(s);
            }
            return ok(out);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    async createTemplate(input) {
        const u = session.user();
        if (!u)
            return err(toDomainError({ code: "permission-denied" }));
        try {
            const id = newId();
            await setDoc(p.template(id), {
                id,
                name: input.name,
                ...(input.emoji !== undefined ? { emoji: input.emoji } : {}),
                ...(input.color !== undefined ? { color: input.color } : {}),
                // Issue #185 — participants (the default travelers) are a separate map from
                // `members` (who the template is shared with); a copy or a "save trip as
                // template" seeds them, an empty template starts with none.
                participants: input.participants ?? {},
                members: { [u.id]: { role: "owner" } },
                memberUserIds: [u.id],
                visibility: "private",
                archived: false,
                createdAt: serverTimestamp(),
            });
            return ok(asTemplateId(id));
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    // Issue #188 — renaming a template, the same inline-title edit a trip has. An
    // ordinary content write on the doc, so an editor may do it (only `members` is
    // owner-gated).
    async renameTemplate(id, name) {
        try {
            await updateDoc(p.template(id), { name });
            return ok(undefined);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    async setTemplateColor(id, color) {
        try {
            await updateDoc(p.template(id), { color });
            return ok(undefined);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    async setTemplateEmoji(id, emoji) {
        try {
            await updateDoc(p.template(id), { emoji });
            return ok(undefined);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    // --- participants (issue #185): the template's default travelers ---
    //
    // Ordinary content writes on the template doc, so an editor may manage them; only
    // `members` is owner-gated. A `user` participant is keyed by its user id (importing
    // the same person from two templates lands on one trip member); a managed one gets a
    // fresh id, which is what the template's `specific` allocations reference.
    async addUserParticipant(id, user) {
        const participantId = user.userId;
        try {
            await updateDoc(p.template(id), {
                [`participants.${participantId}`]: {
                    kind: "user",
                    userId: user.userId,
                    displayName: user.displayName,
                    email: user.email,
                    ...(user.photoUrl !== undefined ? { photoUrl: user.photoUrl } : {}),
                },
            });
            return ok(participantId);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    async addManagedParticipant(id, displayName) {
        const participantId = asManagedProfileId(newId());
        try {
            await updateDoc(p.template(id), {
                [`participants.${participantId}`]: { kind: "managed", displayName },
            });
            return ok(participantId);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    async removeParticipant(id, participantId) {
        try {
            await updateDoc(p.template(id), { [`participants.${participantId}`]: deleteField() });
            return ok(undefined);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    async fetchTemplateParticipants(id) {
        try {
            const snap = await getDoc(p.template(id));
            if (!snap.exists())
                return err(notFound("template", id));
            return ok(participantsToDomain(snap.data().participants));
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    // One-shot read of the template's contents for the import planner (Doc 07 §3).
    // Template categories/items/todos live in the same flat collections as trip
    // contents, keyed by the template id as parent (Doc 07 §2): categories carry
    // `parentId`, items/todos carry the denormalised `listId` = category.parentId.
    async fetchTemplateContents(id) {
        try {
            // The doc read is what carries the participants the import turns into trip
            // members (issue #185).
            const [tplSnap, catSnap, itemSnap, todoSnap] = await Promise.all([
                getDoc(p.template(id)),
                getDocs(query(p.categories(), where("parentId", "==", id))),
                getDocs(query(p.items(), where("listId", "==", id))),
                getDocs(query(p.todos(), where("listId", "==", id))),
            ]);
            const categories = [];
            for (const d of catSnap.docs) {
                const r = toDomainCategory(d.data());
                if (r.ok)
                    categories.push({ id: r.value.id, name: r.value.name });
            }
            const items = [];
            for (const d of itemSnap.docs) {
                const r = toDomainItem(d.data());
                if (!r.ok)
                    continue;
                const i = r.value;
                items.push({
                    id: i.id,
                    categoryId: i.categoryId,
                    name: i.name,
                    quantity: i.quantity,
                    ...(i.description !== undefined ? { description: i.description } : {}),
                    ...(i.notes !== undefined ? { notes: i.notes } : {}),
                    ...(i.tags !== undefined ? { tags: i.tags } : {}),
                    assignment: i.assignment,
                    // Issue #188 — a template row's checkbox only ever stages, and the import
                    // carries that staging onto the trip row it creates.
                    ...(i.staging !== undefined ? { staging: i.staging } : {}),
                });
            }
            const todos = [];
            for (const d of todoSnap.docs) {
                const r = toDomainTodo(d.data());
                if (!r.ok)
                    continue;
                const t = r.value;
                todos.push({
                    id: t.id,
                    categoryId: t.categoryId,
                    name: t.name,
                    ...(t.description !== undefined ? { description: t.description } : {}),
                    ...(t.notes !== undefined ? { notes: t.notes } : {}),
                    ...(t.tags !== undefined ? { tags: t.tags } : {}),
                    assignment: t.assignment,
                    ...(t.relativeDueOffset !== undefined ? { relativeDueOffset: t.relativeDueOffset } : {}),
                });
            }
            const participants = Object.entries(participantsToDomain(tplSnap.data()?.participants)).map(([participantId, participant]) => ({ id: participantId, participant }));
            return ok({ templateId: id, categories, items, todos, participants });
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    // The same three queries as `fetchTemplateContents`, without the `TemplateInput`
    // projection: sync-back compares whole domain rows and needs the category `kind`,
    // `order` and `emoji` the projection drops (Doc 13 §5).
    async fetchTemplateRows(id) {
        try {
            const [catSnap, itemSnap, todoSnap] = await Promise.all([
                getDocs(query(p.categories(), where("parentId", "==", id))),
                getDocs(query(p.items(), where("listId", "==", id))),
                getDocs(query(p.todos(), where("listId", "==", id))),
            ]);
            const categories = [];
            for (const d of catSnap.docs) {
                const r = toDomainCategory(d.data());
                if (r.ok)
                    categories.push(r.value);
            }
            const items = [];
            for (const d of itemSnap.docs) {
                const r = toDomainItem(d.data());
                if (r.ok)
                    items.push(r.value);
            }
            const todos = [];
            for (const d of todoSnap.docs) {
                const r = toDomainTodo(d.data());
                if (r.ok)
                    todos.push(r.value);
            }
            return ok({ categories, items, todos });
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    async archiveTemplate(id, archived) {
        try {
            await updateDoc(p.template(id), { archived });
            return ok(undefined);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
    async deleteTemplate(id) {
        try {
            await deleteDoc(p.template(id));
            return ok(undefined);
        }
        catch (e) {
            return err(toDomainError(e));
        }
    },
});
//# sourceMappingURL=template.repo.js.map